import * as React from "react";
import {Component} from "react";

import {observer} from "mobx-react";

import {Icon} from "../../core/components/icon/icon";
import {Button, Form, Input, Message, Progress, Select, TextArea} from "semantic-ui-react";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";

import "./contractor-form.scss";
import {AppState} from "../../components/app/app-mobx";
import {Contractor} from "../../models/contractor";
import {InputOnChangeData} from "semantic-ui-react/dist/commonjs/elements/Input/Input";
import {ValidationUtils} from "../../utils/validation-utils";

export enum StatusType {
    Saving = "saving",
    Warning = "warning",
    Complete = "successful",
    Error = "error"
}

export interface ContractorFormProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    appStore: AppState;
    auftragsverwaltung: Auftragsverwaltung;
    contractor: Contractor | null;

    onCancel?: (e: React.MouseEvent) => void;
}

@observer
export class ContractorForm extends Component<ContractorFormProps, any> {
    watchHandles: any[] = [];

    constructor(props: ContractorFormProps) {
        super(props);
        this.state = {
            saveError: null,
            saveState: null,
            contractorId: this.props.contractor?.id,
            contractorOrganisation: this.props.contractor?.organisation || "",
            contractorContact: this.props.contractor?.contact || "",
            contractorEmail: this.props.contractor?.email ||  "",
            contractorStreet: this.props.contractor?.street ||  "",
            contractorStreetNumber: this.props.contractor?.streetNumber ||  "",
            contractorPostalCode: this.props.contractor?.postalCode ||  "",
            contractorCity: this.props.contractor?.city ||  "",
            contractorPostbox: this.props.contractor?.postbox ||  "",
            contractorNotes: this.props.contractor?.notes ||  "",
            statusId: this.props.contractor?.statusId || 1,  // set 'Aktiv' as default when not already set
            contractorIdError: false,
            contractorOrganisationError: false,
            contractorContactError: false,
            contractorEmailError: false,
            contractorEmailFormatError: false,
            contractorStreetError: false,
            contractorStreetNumberError: false,
            contractorPostalCodeError: false,
            contractorPostalCodeFormatError: false,
            contractorCityError: false

        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    componentDidMount(): void {
        const {contractor} = this.props;

        if (!contractor || !contractor.id) {
            return;
        }

        this.loadDomains();
    }

    componentWillUnmount(): void {
        /*
         * Avoid `Warning: Can't perform a React state update on an unmounted component. This is a no-op, but it
         * indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks
         * in the componentWillUnmount method.`
         */
        this.setState({});
    }

    render(): React.ReactElement | null {
        const {view, contractor} = this.props;
        const {
            saveError,
            saveState,
            statusOptions,
            contractorId,
            contractorOrganisation,
            contractorContact,
            contractorEmail,
            contractorStreet,
            contractorStreetNumber,
            contractorPostalCode,
            contractorCity,
            contractorPostbox,
            contractorNotes,
            statusId
        } = this.state;
        // const {graphic} = this.state;
        if (!view) {
            return null;
        }
        if (!contractor || !contractor.id) {
            return null;
        }
        let progress: React.ReactElement | null = null;
        if (saveState) {
            progress = (<Progress
                percent={saveState === StatusType.Complete ? 100 : 50}
                attached="bottom"
                active={saveState === StatusType.Saving}
                warning={saveState === StatusType.Warning}
                error={saveState === StatusType.Error}
                success={saveState === StatusType.Complete}
            />);
        }

        return (
            <div className="contractor-form">
                <Form size="huge" onSubmit={() => this.handleSave(contractor)}>
                    <Form.Group widths="equal">
                        {!contractor.isNewRecord &&
                            <Form.Field
                                id="form-input-control-contractor-id"
                                name="contractorId"
                                control={Input}
                                label={{ children: "Auftragnehmer ID", htmlFor: "form-input-control-contractor-id" }}
                                placeholder="z.B. 205790"
                                width={4}
                                value={contractorId}
                                readOnly={!contractor.isNewRecord}
                                onChange={this.handleChange}
                                error={this.state.contractorIdError?
                                    {
                                        content: "Bitte eine gültige ID eingeben",
                                    }:null
                                }
                            />
                        }
                        <Form.Field
                            id="form-input-control-contractor-organisation"
                            name="contractorOrganisation"
                            control={Input}
                            label={{ children: "Organisation", htmlFor: "form-input-control-contractor-organisation" }}
                            placeholder="z.B. geo7"
                            width={12}
                            value={contractorOrganisation}
                            onChange={this.handleChange}
                            error={this.state.contractorOrganisationError?
                                {
                                    content: "Bitte eine Organisation eingeben"
                                }:null
                            }
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field
                            id="form-textarea-control-contractor-contact"
                            name="contractorContact"
                            control={Input}
                            label={{ children: "Kontakt", htmlFor: "form-input-control-contractor-contact" }}
                            placeholder="z.B. Pirmin Zurbriggen"
                            value={contractorContact}
                            onChange={this.handleChange}
                            error={this.state.contractorContactError?
                                {
                                    content: "Bitte einen Kontakt eingeben",
                                }:null
                            }
                        />
                        <Form.Field
                            id="form-input-control-contractor-email"
                            name="contractorEmail"
                            control={Input}
                            label={{ children: "Email", htmlFor: "form-input-control-contractor-email" }}
                            placeholder="lirum.larum@loeffelstiel.ch"
                            value={contractorEmail}
                            onChange={this.handleChange}
                            error={this.state.contractorEmailError || this.state.contractorEmailFormatError?
                                {
                                    content: "Bitte eine gültige Email-Adresse eingeben",
                                }:null
                            }
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field
                            id="form-input-control-contractor-street"
                            name="contractorStreet"
                            control={Input}
                            label={{ children: "Strasse", htmlFor: "form-input-control-contractor-street" }}
                            placeholder="z.B. Murbacherstrasse"
                            value={contractorStreet}
                            onChange={this.handleChange}
                            error={this.state.contractorStreetError?
                                {
                                    content: "Bitte eine Strasse eingeben",
                                }:null
                            }
                        />
                        <Form.Field
                            id="form-input-control-contractor-street-number"
                            name="contractorStreetNumber"
                            control={Input}
                            label={{ children: "Nr", htmlFor: "form-input-control-contractor-street-number" }}
                            placeholder="z.B. 21"
                            value={contractorStreetNumber}
                            onChange={this.handleChange}
                            error={this.state.contractorStreetNumberError?
                                {
                                    content: "Bitte eine Nr eingeben",
                                }:null
                            }
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field
                            id="form-input-control-contractor-postal-code"
                            name="contractorPostalCode"
                            control={Input}
                            label={{ children: "PLZ", htmlFor: "form-input-control-contractor-postal-code" }}
                            placeholder="z.B. 6002"
                            value={contractorPostalCode}
                            onChange={this.handleChange}
                            error={this.state.contractorPostalCodeError || this.state.contractorPostalCodeFormatError?
                                {
                                    content: "Bitte eine gültige PLZ eingeben",
                                }:null
                            }
                        />
                        <Form.Field
                            id="form-input-control-contractor-city"
                            name="contractorCity"
                            control={Input}
                            label={{ children: "Ort", htmlFor: "form-input-control-contractor-city" }}
                            placeholder="z.B. Luzern"
                            value={contractorCity}
                            onChange={this.handleChange}
                            error={this.state.contractorCityError?
                                {
                                    content: "Bitte eine gültigen Ort eingeben",
                                }:null
                            }
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field
                            id="form-input-control-contractor-postbox"
                            name="contractorPostbox"
                            control={Input}
                            label={{ children: "Postfach", htmlFor: "form-input-control-contractor-postbox" }}
                            placeholder=""
                            value={contractorPostbox}
                            onChange={this.handleChange}
                        />
                        <Form.Field
                            id="form-textarea-control-contractor-notes"
                            name="contractorNotes"
                            control={TextArea}
                            label={{ children: "Bemerkungen", htmlFor: "form-textarea-control-contractor-notes" }}
                            placeholder=""
                            value={contractorNotes}
                            onChange={this.handleChange}
                        />
                    </Form.Group>
                    {statusOptions &&
                        <Form.Field
                            name="statusId"
                            control={Select}
                            options={statusOptions}
                            label={{ children: "Status", htmlFor: "form-select-control-contractor-status" }}
                            placeholder={"Aktiv"}
                            search
                            searchInput={{ id: "form-select-control-contractor-status" }}
                            value={statusId}
                            onChange={this.handleChange}
                        />
                    }
                    {progress}
                    {saveState && saveState === StatusType.Error &&
                        <Message attached="bottom" negative>
                            <Icon name={"error"} />
                            Fehler beim Speichern! {saveError}
                        </Message>
                    }
                    <Button className={"save"} primary type="submit"
                    >
                        <Icon name={"floppy_disk"} />
                        Speichern
                    </Button>

                    <Button className={"cancel"} secondary
                        onClick={(e: React.MouseEvent) => {
                            if (this.props.onCancel) {
                                this.props.onCancel(e);
                            }
                        }}>
                        <Icon name={"navigate_cross"} />
                        Abbrechen
                    </Button>
                </Form>
            </div>
        );
    }

    private handleChange(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData): void {
        this.setState({[data.name]: data.value});
    };

    private handleSave(contractor: Contractor): void {
        const {appStore, auftragsverwaltung} = this.props;
        const {contractorId, contractorOrganisation, contractorContact, contractorEmail, contractorStreet,
            contractorStreetNumber, contractorPostalCode, contractorCity, contractorPostbox, contractorNotes,
        } = this.state;

        const error = this.validateFormInput();
        if (error) {
            this.setState({formError: true});
            return;
        }

        this.setState({formError: false});

        // make progress indicator visible
        this.setState({
            saveState: StatusType.Saving
        });

        // autoincrement project id for risk projects
        this.autoIncrementId(contractor).then((id) => {

            const updated = {
                id: id || parseInt(contractorId, 10),
                organisation: contractorOrganisation,
                contact: contractorContact,
                email: contractorEmail,
                street: contractorStreet,
                streetNumber: contractorStreetNumber,
                postalCode: contractorPostalCode,
                city: contractorCity,
                postbox: contractorPostbox,
                notes: contractorNotes
            };

            // change contractor ID to final ID
            if (contractor) {
                contractor.id = updated.id;
            }

            const user = appStore.esriCredential?.userId;
            auftragsverwaltung.ContractorStore.save(contractor, updated, user || "")
                .then(() => {
                    this.setState({
                        saveState: StatusType.Complete,
                        saveError: null
                    });
                    appStore.setObjectId(String(updated.id));
                    auftragsverwaltung.setStatus("successful");
                    auftragsverwaltung.setEditing(false);
                }).catch((error) => {
                    this.setState({
                        saveState: StatusType.Error,
                    saveError: error.message
                    });
                });
        });
    }

    private autoIncrementId(contractor: Contractor): Promise<number | null> {
        const {auftragsverwaltung} = this.props;

        return new Promise((resolve) => {
            if (!contractor.isNewRecord) {
                // do not auto increment. use existing ID
                resolve(null);
            }

            const currentYear = new Date().getFullYear();
            auftragsverwaltung.ContractorStore.maxId(currentYear).then((id) => {
                if (!id || !id.toString().startsWith(currentYear.toString())) {
                    // start ID not used yet
                    // do also start a new number if last id does belong to another year
                    resolve(parseInt(`${currentYear}001`, 10));
                    return;
                }
                // increment
                resolve(id + 1);
                return;
            });
        });
    }

    /**
     * Load domains and sources for select boxes.
     *
     * @private
     */
    private loadDomains() {
        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.loadDomain("contractor", "statusId").then((statusDomain) =>{
            const statusOptions = statusDomain?.codedValues.map((codedValue: __esri.CodedValue) => ({
                key: codedValue.code,
                text: codedValue.name,
                value: codedValue.code
            }));

            this.setState({
                statusOptions
            });
        });
    }

    private validateFormInput(): boolean {
        const {contractorId, contractorOrganisation, contractorContact, contractorEmail, contractorStreet,
            contractorStreetNumber, contractorPostalCode, contractorCity
        } = this.state;

        if (contractorId === "") {
            this.setState({contractorIdError: true});
            return true;
        } else {
            this.setState({contractorIdError: false});
        }

        if (contractorOrganisation === "") {
            this.setState({contractorOrganisationError: true});
            return true;
        } else {
            this.setState({contractorOrganisationError: false});
        }

        if (contractorContact === "") {
            this.setState({contractorContactError: true});
            return true;
        } else {
            this.setState({contractorContactError: false});
        }

        if (contractorEmail === "") {
            this.setState({contractorEmailError: true});
            return true;
        } else {
            this.setState({contractorEmailError: false});
        }
        if (!ValidationUtils.isValidEmail(contractorEmail)) {
            this.setState({contractorEmailFormatError: true});
            return true;
        } else {
            this.setState({contractorEmailFormatError: false});
        }

        if (contractorStreet === "") {
            this.setState({contractorStreetError: true});
            return true;
        } else {
            this.setState({contractorStreetError: false});
        }

        if (contractorStreetNumber === "") {
            this.setState({contractorStreetNumberError: true});
            return true;
        } else {
            this.setState({contractorStreetNumberError: false});
        }

        if (contractorPostalCode === "") {
            this.setState({contractorPostalCodeError: true});
            return true;
        } else {
            this.setState({contractorPostalCodeError: false});
        }
        if (!ValidationUtils.isValidPostalCode(contractorPostalCode)) {
            this.setState({contractorPostalCodeFormatError: true});
            return true;
        } else {
            this.setState({contractorPostalCodeFormatError: false});
        }

        if (contractorCity === "") {
            this.setState({contractorCityError: true});
            return true;
        } else {
            this.setState({contractorCityError: false});
        }
        return false;
    }
}
